/**
 * Partner-autocomplete — cég + magánszemély keresés + inline magánszemély felvétel
 */
import { useState, useEffect, useRef } from 'react';
import { get, post } from '../lib/api';
import { useToast } from './ui/Toast';
import Input from './ui/Input';
import Button from './ui/Button';
import AddressForm, { emptyAddress } from './AddressForm';
import type { AddressData } from './AddressForm';

interface Partner {
  id: string;
  name: string;
  tax_number?: string;
  kind?: string;
  city?: string;
  address_street_name?: string;
  address_house_number?: string;
  address_postal_code?: string;
}

interface Props {
  value: Partner | null;
  onChange: (partner: Partner | null) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
}

export default function PartnerAutocomplete({ value, onChange, placeholder = 'Keress partnert...', label, required }: Props) {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showInlineForm, setShowInlineForm] = useState(false);
  const [inlineForm, setInlineForm] = useState({ name: '', email: '', address: { ...emptyAddress } as AddressData });
  const [inlineSaving, setInlineSaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Keresés — mindig cégek + magánszemélyek együtt
  useEffect(() => {
    if (!open) return;
    const q = search.length >= 1 ? `search=${encodeURIComponent(search)}&` : '';
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await get<any>(`/partners?${q}pageSize=15`);
        setResults(Array.isArray(data) ? data : data?.data || []);
      } catch { setResults([]); }
      setLoading(false);
    }, search.length >= 1 ? 250 : 0);
    return () => clearTimeout(timer);
  }, [search, open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setShowInlineForm(false); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function buildAddressLine(p: Partner): string {
    return [p.address_postal_code, p.city, [p.address_street_name, p.address_house_number].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  }

  async function handleInlineSave() {
    if (!inlineForm.name.trim()) { toast('A név kötelező.', 'error'); return; }
    setInlineSaving(true);
    try {
      const hasAddress = inlineForm.address.city && inlineForm.address.postalCode;
      const created = await post<Partner>('/partners', {
        name: inlineForm.name,
        kind: 'individual',
        isCustomer: true,
        email: inlineForm.email || undefined,
        addresses: hasAddress ? [{
          postalCode: inlineForm.address.postalCode,
          city: inlineForm.address.city,
          streetName: inlineForm.address.streetName || undefined,
          streetType: inlineForm.address.streetType || undefined,
          houseNumber: inlineForm.address.houseNumber || undefined,
          building: inlineForm.address.building || undefined,
          floor: inlineForm.address.floor || undefined,
          door: inlineForm.address.door || undefined,
          isPrimary: true,
        }] : undefined,
      });
      toast(`${inlineForm.name} létrehozva.`, 'success');
      onChange(created);
      setOpen(false);
      setShowInlineForm(false);
      setSearch('');
      setInlineForm({ name: '', email: '', address: { ...emptyAddress } });
    } catch (e: any) { toast(e.message || 'Mentés sikertelen.', 'error'); }
    finally { setInlineSaving(false); }
  }

  const companyResults = results.filter(p => p.kind !== 'individual');
  const individualResults = results.filter(p => p.kind === 'individual');
  const noResults = !loading && results.length === 0 && search.length >= 2;
  const showDropdown = open && (results.length > 0 || loading || noResults || showInlineForm);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {label && <label className="form-label">{label}{required ? ' *' : ''}</label>}
      <input
        className="form-input"
        value={value ? value.name : search}
        onChange={e => { setSearch(e.target.value); setOpen(true); setShowInlineForm(false); if (value) onChange(null); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        style={{ minHeight: '38px', width: '100%' }}
      />
      {showDropdown && (
        <div onMouseDown={e => e.preventDefault()} style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px',
          background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 'var(--r)',
          boxShadow: '0 12px 30px -10px rgba(20,35,63,.3)',
          maxHeight: showInlineForm ? '80vh' : '320px', overflowY: 'auto', zIndex: 200,
        }}>
          {loading && <div style={{ padding: '12px', color: 'var(--slate)', fontSize: '13px' }}>Keresés...</div>}

          {/* Cégek */}
          {companyResults.map(p => (
            <button key={p.id} type="button" onClick={() => { onChange(p); setOpen(false); setSearch(''); }}
              className="dd-item" style={{ width: '100%', textAlign: 'left' }}>
              <span style={{ fontSize: '14px', width: '20px', textAlign: 'center', flexShrink: 0 }}>🏢</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--navy)' }}>{p.name}</div>
                <div style={{ fontSize: '10px', color: 'var(--slate)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.tax_number || ''}{p.city ? (p.tax_number ? ' · ' : '') + buildAddressLine(p) : ''}
                </div>
              </span>
            </button>
          ))}

          {/* Magánszemélyek */}
          {individualResults.length > 0 && (
            <>
              {companyResults.length > 0 && (
                <div style={{ padding: '6px 12px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--slate)', fontWeight: 700, background: 'var(--mist)', borderTop: '1px solid var(--line-2)' }}>
                  Magánszemélyek
                </div>
              )}
              {individualResults.map(p => (
                <button key={p.id} type="button" onClick={() => { onChange(p); setOpen(false); setSearch(''); }}
                  className="dd-item" style={{ width: '100%', textAlign: 'left' }}>
                  <span style={{ fontSize: '14px', width: '20px', textAlign: 'center', flexShrink: 0 }}>👤</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--navy)' }}>{p.name}</div>
                    <div style={{ fontSize: '10px', color: 'var(--slate)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {buildAddressLine(p) || 'Magánszemély'}
                    </div>
                  </span>
                </button>
              ))}
            </>
          )}

          {/* Nincs találat → gyors magánszemély felvétel */}
          {noResults && !showInlineForm && (
            <div style={{ padding: '12px' }}>
              <div style={{ fontSize: '13px', color: 'var(--slate)', marginBottom: '8px' }}>Nincs találat „{search}" keresésre.</div>
              <button type="button" onClick={() => { setShowInlineForm(true); setInlineForm(f => ({ ...f, name: search })); }}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 'var(--r)',
                  border: '1px dashed var(--orange)', background: '#fef7ed',
                  cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px',
                }}>
                <span style={{ fontSize: '20px' }}>👤</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--orange)' }}>+ Értékesítés magánszemélynek</div>
                  <div style={{ fontSize: '11px', color: 'var(--slate)' }}>Gyors felvétel: név, cím, e-mail</div>
                </div>
              </button>
            </div>
          )}

          {/* Inline magánszemély form */}
          {showInlineForm && (
            <div style={{ padding: '14px', borderTop: '1px solid var(--line-2)' }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--orange)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                👤 Új magánszemély felvétele
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Input label="Teljes név *" value={inlineForm.name} onChange={e => setInlineForm(f => ({ ...f, name: e.target.value }))} placeholder="Kovács János" />
                <AddressForm value={inlineForm.address} onChange={addr => setInlineForm(f => ({ ...f, address: addr }))} />
                <Input label="E-mail" type="email" value={inlineForm.email} onChange={e => setInlineForm(f => ({ ...f, email: e.target.value }))} placeholder="janos@email.hu" />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginTop: '4px' }}>
                  <Button size="sm" variant="ghost" onClick={() => setShowInlineForm(false)}>Mégse</Button>
                  <Button size="sm" onClick={handleInlineSave} loading={inlineSaving}>Létrehozás és kiválasztás</Button>
                </div>
              </div>
            </div>
          )}

          {/* "Új magánszemély" gomb alul ha van találat de nem keresett */}
          {!showInlineForm && !noResults && !loading && results.length > 0 && (
            <div style={{ padding: '8px 12px', borderTop: '1px solid var(--line-2)', textAlign: 'center' }}>
              <button type="button" onClick={() => { setShowInlineForm(true); setInlineForm(f => ({ ...f, name: search })); }}
                style={{ fontSize: '12px', color: 'var(--orange)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>
                + Új magánszemély felvétele
              </button>
            </div>
          )}
        </div>
      )}
      {required && <input type="text" value={value?.id || ''} required style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} tabIndex={-1} />}
    </div>
  );
}
